import get from 'lodash/get';
import clone from 'lodash/cloneDeep';
import set from 'lodash/set';
import { mapState } from 'vuex';
import isEqual from 'lodash/isEqual';
import { VRow, VCol, VExpansionPanel, VExpansionPanelContent, VExpansionPanelHeader, VExpansionPanels, VSpacer } from 'vuetify/lib';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { templateMixin } from './templateMixin';
export default {
  name: 'CoreDetailsPage',
  components: {
    VRow,
    VCol,
    VExpansionPanel,
    VExpansionPanels,
    VExpansionPanelHeader,
    VExpansionPanelContent,
    VSpacer
  },
  mixins: [templateMixin],
  props: {
    /**
     * Function to fetch the item to display
     * @type {Function}
     * @required
     * @return {Promise} - The item to display
     */
    fetchItem: {
      type: Function,
      required: true
    },
    /**
     * External fetch parameters to be passed into fetchItem(), changes will trigger a refetch
     * @type {Object}
     * @default {}
     */
    externalFetchParams: {
      type: Object,
      default: () => ({})
    },
    /**
     * Function to save the updated item
     * @type {Function}
     * @arg {Object} item - The updated item
     * @required
     * @return {Promise} - The updated item
     */
    saveItem: {
      type: Function,
      required: true
    },
    /**
     * The headline of the page
     * @type {String} | {Function}
     * @required
     * @return {String}
     */
    headline: {
      type: [String, Function],
      required: true
    },
    /**
     * The subtitle of the page
     * @type {String} | {Function}
     * @required
     * @return {String}
     */
    subtitle: {
      type: [String, Function],
      required: true
    },
    /**
     * Tabs to show on the page
     * @type {Array} - Array of objects with the following properties:
     *  - label {String} - The label of the tab
     *  - urlHashbang {String} - The hashbang to use for the tab
     *  - rows {Array} - Array of objects with the following properties:
     *    - class {String} - The class to apply to the row
     *    - columns {Array} - Array of objects with the following properties:
     *      - cols {Number} - The number of columns to span
     *      - sm {Number} - The number of columns to span on small screens
     *      - md {Number} - The number of columns to span on medium screens
     *      - lg {Number} - The number of columns to span on large screens
     *      - class {String} - The class to apply to the column
     *      - field {Object} - The field to display in the column with the following properties:
     *         - label {String} - The label of the field
     *         - key {String} - The key of the field in the item
     *         - icon {String} - The icon to display next to the field
     *         - type {String} - The type of the field
     *         - if {Function} - Function to determine if the field should be displayed
     *      - rows {Array} - Array of objects with the same properties as parent rows
     * @required
     */
    tabs: {
      type: Array,
      required: true
    },
    /**
     * Whether the page is in edit mode
     * @type {Boolean}
     * @default false
     */
    editMode: {
      type: Boolean,
      default: false
    },
    /**
     * Header buttons
     * @type {Array} [{ dataCy?: string, color?: string, type?: string, tooltip?: string, href?: string, target?: string, icon?: string, action?: string }]
     */
    headerButtons: {
      type: [Array, Function],
      default: () => []
    },
    /**
     * The href to navigate to when closing the page
     * @type {String}
     */
    parentHref: {
      type: String,
      default: null
    },
    /**
     * Function to map the redirect URL after saving the item (gets the created item as argument)
     * @type {Function}
     * @default null
     * @return {String} - The redirect URL
     */
    postCreateRedirectUrlMapper: {
      type: Function,
      default: null
    },
    isFormValid: {
      type: Boolean,
      default: true
    },
    externalIsChanged: {
      type: Boolean,
      default: false
    },
    /**
     * The name of the item, used e.g. in snack messages
     * @type {String}
     */
    itemName: {
      type: String,
      default: 'Item'
    },
    templateInformation: {
      type: Object,
      default: () => ({})
    }
  },
  data: function () {
    return {
      tabItem: this.$route.query.tab,
      isFetching: false,
      item: {},
      originalItem: {},
      showUnsavedPrompt: false,
      formIsValid: true,
      isSaving: false,
      fetchError: null,
      synchronisationStatus: null,
      showDraftVersion: true
    };
  },
  computed: {
    ...mapState('app', ['color']),
    itemIsChanged() {
      if (this.externalIsChanged) {
        return true;
      }
      if (!this.item || !this.originalItem) {
        console.warn('item or originalItem is not set');
        return false;
      }
      // we need to stringify and parse the objects to avoid reference comparison
      return !isEqual(JSON.parse(JSON.stringify(this.item)), JSON.parse(JSON.stringify(this.originalItem)));
    },
    isEditable() {
      return this.editMode && !this.isSaving;
    },
    computedTabs() {
      return this.tabs.map(tab => {
        if (typeof tab === 'function') {
          return tab(this.item, this.originalItem);
        }
        return tab;
      });
    },
    computedHeaderButtons() {
      if (typeof this.headerButtons === 'function') {
        return this.headerButtons(this.item, this.originalItem);
      }
      return this.headerButtons;
    },
    switchLabel() {
      if (this.showDraftVersion) {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.versionSwitchOn');
      } else {
        return this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.versionSwitchOff');
      }
    },
    linkedDevicesTotalCountSafe() {
      const linkedDevicesTotalCount = Number(this.templateInformation.linkedDevicesTotalCount);
      if (!Number.isFinite(linkedDevicesTotalCount)) {
        return 0;
      }
      return Math.max(linkedDevicesTotalCount, 0);
    }
  },
  watch: {
    '$route.query.tab': function (newValue) {
      if (newValue === this.tabItem) {
        return;
      }
      this.tabItem = newValue;
    },
    tabItem() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab: this.tabItem
        }
      });
    },
    externalFetchParams: {
      handler: function (newParams, oldParams) {
        if (isEqual(newParams, oldParams)) {
          return;
        }
        this.callFetchItem();
      },
      deep: true
    },
    itemIsChanged() {
      this.$emit('item-change-state', this.itemIsChanged);
    },
    showDraftVersion() {
      this.handleSwitchVersion(this.showDraftVersion);
    }
  },
  created() {
    this.callFetchItem(true);
  },
  methods: {
    get,
    isEmpty,
    computeFunctionStringParam(val, data) {
      if (typeof val === 'undefined' || val === null) {
        return undefined;
      }
      return typeof val === 'function' ? val(data) : val;
    },
    parseAndSnackError(error) {
      console.error(error);
      if (error && error.skipSnack) {
        return null;
      }
      let translationKey = get(error, 'response.data.message', get(error, 'message', '_unknown_'));
      if (translationKey === 'Network Error') {
        translationKey = 'NETWORK_ERROR';
      }
      const details = {};
      const errorKey = get(error, 'response.data.message');
      const isParameterValidationError = errorKey === 'Parameters validation error!';
      if (isParameterValidationError) {
        translationKey = 'PARAMETER_VALIDATION_ERROR';
        details.errors = get(error, 'response.data.data', []).map(e => e.message);
        details.errorsJoined = details.errors.join(', ');
      }
      this.$snack(this.$i18n_t(`Error.${translationKey}`, details), 'error');
      return {
        translationKey,
        details
      };
    },
    async callFetchItem(showDraftVersion) {
      try {
        this.isFetching = true;
        this.item = await this.fetchItem({
          ...this.externalFetchParams,
          productiveVersion: showDraftVersion ? false : true
        });
        if (!isEmpty(this.templateInformation)) {
          this.item.isProductiveVersion = !showDraftVersion;
          this.synchronisationStatus = this.item.deviceSyncStatus;
        }
        this.originalItem = clone(this.item);
        this.fetchError = null;
      } catch (error) {
        const parsedError = this.parseAndSnackError(error);
        this.fetchError = parsedError;
      } finally {
        this.isFetching = false;
      }
    },
    updateItemByFieldKey(fieldKey, value) {
      const updatedItem = cloneDeep(this.item);
      set(updatedItem, fieldKey, value);
      this.item = updatedItem;
    },
    onCloseClick(ignoreUnsavedChanges = false) {
      if (!ignoreUnsavedChanges && this.itemIsChanged) {
        this.showUnsavedPrompt = true;
      } else {
        this.$router.push(this.parentHref);
      }
    },
    discardChangesAndClose() {
      this.onCloseClick(true);
    },
    async onSaveClick() {
      try {
        const isValid = this.$refs.form.validate();
        if (!isValid) {
          this.$snack(this.$i18n_t('Core.DetailsPage.snacks.saveError.invalidForm'), 'error');
          return;
        }
        this.isSaving = true;
        this.item = await this.saveItem(this.item);
        this.originalItem = clone(this.item);
        if (this.$route.path.endsWith('/new')) {
          let redirectURL = this.$route.path.replace('/new', `/${this.item.id}`);
          if (typeof this.postCreateRedirectUrlMapper === 'function') {
            redirectURL = this.postCreateRedirectUrlMapper(this.item);
          }
          this.$router.push(redirectURL);
        }
        this.$snack(this.$i18n_t('Core.DetailsPage.snacks.saveSuccess', {
          itemName: this.itemName
        }), 'success');
      } catch (error) {
        this.parseAndSnackError(error);
      } finally {
        this.isSaving = false;
      }
    },
    async resetItem() {
      await this.callFetchItem();
    },
    onUnsavedChangesDialogButton(buttonId) {
      switch (buttonId) {
        case 'cancel':
          this.showUnsavedPrompt = false;
          break;
        case 'discard-and-close':
          this.discardChangesAndClose();
          break;
        case 'close-page':
          this.onCloseClick(true);
          break;
      }
    },
    handleSwitchVersion(showDraftVersion) {
      this.callFetchItem(showDraftVersion);
    }
  }
};